import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Divider,
  IconButton,
  Button,
  TablePagination,
  Grid,
  Stack,
  Tooltip as MuiTooltip
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  Scanner as ScannerIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import axios from 'axios';
import { getScanSummaryToday, getTodayScanRequests } from '../BaseURL';

const ScanHome = ({ onEditScan }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalRequests, setTotalRequests] = useState(0);
  const [completedResults, setCompletedResults] = useState(0);
  const [scanResultsData, setScanResultsData] = useState([]);
  const [todayRequestsList, setTodayRequestsList] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2'];

  useEffect(() => {
    fetchScanSummary();
    fetchTodayRequests();
  }, []);

  const fetchScanSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(getScanSummaryToday);

      if (response.data.success) {
        setTotalRequests(response.data.totalRequests || 0);
        setCompletedResults(response.data.completedResults || 0);
        
        if (response.data.scanResults && response.data.scanResults.length > 0) {
          const chartData = response.data.scanResults.map(scan => ({
            name: scan.scanName,
            count: scan.resultCount
          }));
          setScanResultsData(chartData);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching scan summary:', error);
      setError('Failed to load scan summary');
      setLoading(false);
    }
  };

  const fetchTodayRequests = async () => {
    try {
      setRequestsLoading(true);

      const response = await axios.get(getTodayScanRequests);

      if (response.data.success) {
        const requests = response.data.requests || [];
        // Sort by requested time (most recent first)
        requests.sort((a, b) => new Date(b.requested_date + ' ' + b.requested_time) - new Date(a.requested_date + ' ' + a.requested_time));

        setTodayRequestsList(requests);
      }

      setRequestsLoading(false);
    } catch (error) {
      console.error('Error fetching today requests:', error);
      setRequestsLoading(false);
    }
  };

  const getCompletionPercentage = () => {
    if (totalRequests === 0) return 0;
    return Math.round((completedResults / totalRequests) * 100);
  };

  const getScanStatusColor = (index) => {
    return COLORS[index % COLORS.length];
  };

  const handleEditScan = (scanRequest) => {
    if (onEditScan) {
      // Format the scan request data to match what RequestedScan expects
      const scanData = {
        id: scanRequest.trans_code,
        patient_id: scanRequest.id,
        scan_name: scanRequest.scan_name,
        scan_code: scanRequest.scan_code,
        requested_date: scanRequest.requested_date,
        requested_time: scanRequest.requested_time,
        doc_name: scanRequest.doc_name,
        full_name: scanRequest.full_name,
        patient_type: scanRequest.patient_type,
        status: 'pending' // Default status
      };
      
      onEditScan(scanData);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const stats = [
    {
      key: 'total',
      label: 'Total Requests',
      value: totalRequests,
      helper: 'received today',
      icon: <ScheduleIcon sx={{ color: 'white', fontSize: 22 }} />,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      shadow: '0 4px 18px rgba(102, 126, 234, 0.32)',
      shadowHover: '0 10px 28px rgba(102, 126, 234, 0.42)',
    },
    {
      key: 'saved',
      label: 'Results Saved',
      value: completedResults,
      helper: 'completed reports',
      icon: <CheckCircleIcon sx={{ color: 'white', fontSize: 22 }} />,
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      shadow: '0 4px 18px rgba(245, 87, 108, 0.32)',
      shadowHover: '0 10px 28px rgba(245, 87, 108, 0.42)',
    },
    {
      key: 'rate',
      label: 'Completion Rate',
      value: `${getCompletionPercentage()}%`,
      helper: `${completedResults} of ${totalRequests}`,
      icon: <TrendingUpIcon sx={{ color: 'white', fontSize: 22 }} />,
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      shadow: '0 4px 18px rgba(79, 172, 254, 0.32)',
      shadowHover: '0 10px 28px rgba(79, 172, 254, 0.42)',
    },
    {
      key: 'types',
      label: 'Scan Types',
      value: scanResultsData.length,
      helper: 'distinct categories',
      icon: <AssessmentIcon sx={{ color: 'white', fontSize: 22 }} />,
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      shadow: '0 4px 18px rgba(250, 112, 154, 0.32)',
      shadowHover: '0 10px 28px rgba(250, 112, 154, 0.42)',
    },
  ];

  const renderChartCard = ({ title, subtitle, color, tint, icon, hasData, children }) => (
    <Paper elevation={0} sx={{
      borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden',
      bgcolor: 'background.paper', height: '100%', width: '100%',
    }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 2.5, py: 1.75,
        borderLeft: '4px solid', borderLeftColor: color,
        bgcolor: tint,
      }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 1.75,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: color, color: 'white',
          boxShadow: `0 4px 10px ${color}40`,
        }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontWeight: 700, color: 'text.primary',
            fontSize: '0.95rem', lineHeight: 1.2,
          }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary', mt: 0.25 }}>
            {subtitle}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ p: 2.5, minHeight: 400 }}>
        {hasData ? children : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: '50%',
              bgcolor: 'action.hover', mx: 'auto', mb: 1.5,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AssessmentIcon sx={{ color: 'text.secondary' }} />
            </Box>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', fontWeight: 600 }}>
              No data yet
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.78rem', mt: 0.25 }}>
              Results will appear here once saved today.
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{
          fontSize: '0.72rem', fontWeight: 700,
          color: '#1976d2', letterSpacing: '1.4px',
          textTransform: 'uppercase', mb: 0.5,
        }}>
          Radiology Overview
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 2,
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(25,118,210,0.32)',
          }}>
            <ScannerIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography sx={{
              fontSize: { xs: '1.4rem', md: '1.7rem' },
              fontWeight: 700, color: 'text.primary',
              letterSpacing: '-0.4px', lineHeight: 1.1,
            }}>
              Scan Dashboard
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mt: 0.25 }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </Typography>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
          <CircularProgress sx={{ color: '#1976d2' }} />
          <Typography sx={{ color: 'text.secondary', mt: 2, fontSize: '0.9rem' }}>
            Loading today's overview…
          </Typography>
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {stats.map((s) => (
              <Grid item xs={12} sm={6} md={3} key={s.key}>
                <Paper elevation={0} sx={{
                  p: 2.5, borderRadius: 3, color: 'white',
                  backgroundImage: `${s.gradient} !important`,
                  backgroundColor: 'transparent',
                  boxShadow: s.shadow,
                  position: 'relative', overflow: 'hidden',
                  height: '100%',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': { transform: 'translateY(-3px)', boxShadow: s.shadowHover }
                }}>
                  {/* decorative glow */}
                  <Box sx={{
                    position: 'absolute', top: -40, right: -40,
                    width: 140, height: 140, borderRadius: '50%',
                    bgcolor: 'rgba(255,255,255,0.12)',
                    pointerEvents: 'none',
                  }} />
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ position: 'relative' }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{
                        fontSize: '0.7rem', fontWeight: 700,
                        letterSpacing: '1.2px', textTransform: 'uppercase',
                        opacity: 0.92,
                      }}>
                        {s.label}
                      </Typography>
                      <Typography sx={{
                        fontSize: '2.4rem', fontWeight: 800,
                        mt: 0.5, lineHeight: 1,
                        textShadow: '0 2px 6px rgba(0,0,0,0.18)',
                      }}>
                        {s.value}
                      </Typography>
                      <Typography sx={{ fontSize: '0.78rem', opacity: 0.88, mt: 1 }}>
                        {s.helper}
                      </Typography>
                    </Box>
                    <Box sx={{
                      width: 44, height: 44, borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.22)',
                      border: '1px solid rgba(255,255,255,0.32)',
                      backdropFilter: 'blur(10px)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      {s.icon}
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Charts Section */}
          <Box sx={{
            mb: 3,
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            alignItems: 'stretch',
          }}>
            <Box sx={{ minWidth: 0 }}>
              {renderChartCard({
                title: 'Results by Scan Type',
                subtitle: 'Number of completed reports per category',
                color: '#1976d2',
                tint: 'rgba(25,118,210,0.10)',
                icon: <ScannerIcon sx={{ fontSize: 20 }} />,
                hasData: scanResultsData.length > 0,
                children: (
                  <ResponsiveContainer width="100%" height={380}>
                    <BarChart data={scanResultsData} margin={{ top: 10, right: 16, left: 0, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ef" vertical={false} />
                      <XAxis dataKey="name" angle={-35} textAnchor="end" interval={0} tick={{ fontSize: 11, fill: '#64748b' }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                      <Tooltip
                        contentStyle={{ borderRadius: 8, border: '1px solid #e5e9ef', fontSize: '0.85rem' }}
                        cursor={{ fill: 'rgba(25,118,210,0.06)' }}
                      />
                      <Bar dataKey="count" fill="#1976d2" name="Reports" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ),
              })}
            </Box>

            <Box sx={{ minWidth: 0 }}>
              {renderChartCard({
                title: 'Results Distribution',
                subtitle: 'Share of each scan type in today\'s output',
                color: '#9333ea',
                tint: 'rgba(147,51,234,0.12)',
                icon: <TrendingUpIcon sx={{ fontSize: 20 }} />,
                hasData: scanResultsData.length > 0,
                children: (
                  <ResponsiveContainer width="100%" height={380}>
                    <PieChart>
                      <Pie
                        data={scanResultsData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, count }) => `${name}: ${count}`}
                        outerRadius={120}
                        dataKey="count"
                        stroke="#fff"
                        strokeWidth={2}
                      >
                        {scanResultsData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getScanStatusColor(index)} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e5e9ef', fontSize: '0.85rem' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ),
              })}
            </Box>
          </Box>

          {/* Today's Requests Table */}
          <Paper elevation={0} sx={{
            borderRadius: 3, border: '1px solid', borderColor: 'divider',
            overflow: 'hidden', bgcolor: 'background.paper',
          }}>
            {/* Header strip */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: 2.5, py: 1.75,
              borderBottom: '1px solid', borderColor: 'divider',
              bgcolor: 'action.hover',
            }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: 1.75,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                color: 'white',
                boxShadow: '0 4px 10px rgba(25,118,210,0.32)',
              }}>
                <ScheduleIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.98rem', lineHeight: 1.2 }}>
                  Today's Scan Requests
                </Typography>
                <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary', mt: 0.25 }}>
                  Click the pencil to enter results for any request
                </Typography>
              </Box>
              <Chip
                label={`${todayRequestsList.length} request${todayRequestsList.length === 1 ? '' : 's'}`}
                size="small"
                sx={{
                  fontWeight: 700, height: 26,
                  bgcolor: 'rgba(25,118,210,0.18)', color: '#60a5fa',
                  border: '1px solid rgba(96,165,250,0.30)',
                }}
              />
            </Box>

            {requestsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} sx={{ color: '#1976d2' }} />
              </Box>
            ) : todayRequestsList.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
                <Box sx={{
                  width: 64, height: 64, borderRadius: '50%',
                  bgcolor: 'action.hover', mx: 'auto', mb: 2,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <ScannerIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                </Box>
                <Typography sx={{ color: 'text.primary', fontWeight: 600 }}>
                  No scan requests for today
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mt: 0.5 }}>
                  Requests from doctors will appear here as they're submitted.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 780 }}>
                  <TableHead>
                    <TableRow sx={{ '& th': {
                      bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider',
                      fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary',
                      textTransform: 'uppercase', letterSpacing: '0.5px',
                      py: 1.25,
                    }}}>
                      <TableCell>Patient ID</TableCell>
                      <TableCell>Patient</TableCell>
                      <TableCell>Scan Type</TableCell>
                      <TableCell>Requested By</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {todayRequestsList
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((request, index) => {
                        const isInpatient = String(request.patient_type).toLowerCase() === 'inpatient';
                        return (
                          <TableRow
                            key={index}
                            hover
                            sx={{
                              '& td': { borderBottom: '1px solid', borderColor: 'divider', py: 1.25 },
                              '&:last-child td': { borderBottom: 'none' },
                              transition: 'background-color 0.15s ease',
                            }}
                          >
                            <TableCell>
                              <Typography component="span" sx={{
                                fontWeight: 600, color: '#60a5fa',
                                fontFamily: 'monospace', fontSize: '0.82rem',
                                bgcolor: 'rgba(25,118,210,0.18)', px: 1, py: 0.25,
                                borderRadius: 1,
                                border: '1px solid rgba(96,165,250,0.30)',
                              }}>
                                {request.id}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1.25} alignItems="center">
                                <Avatar sx={{
                                  width: 32, height: 32, fontSize: 12, fontWeight: 700,
                                  bgcolor: 'rgba(25,118,210,0.12)', color: '#1976d2',
                                }}>
                                  {request.full_name?.charAt(0)?.toUpperCase() || 'P'}
                                </Avatar>
                                <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: 'text.primary' }}>
                                  {request.full_name}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={request.scan_name || request.lab}
                                size="small"
                                sx={{
                                  fontWeight: 600, height: 22,
                                  bgcolor: 'rgba(147,51,234,0.18)', color: '#c084fc',
                                  border: '1px solid rgba(192,132,252,0.30)',
                                  '& .MuiChip-label': { px: 1 },
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                                {request.doc_name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={request.patient_type}
                                size="small"
                                sx={{
                                  fontWeight: 700, height: 22,
                                  bgcolor: isInpatient ? '#DCFCE7' : '#FEF3C7',
                                  color:   isInpatient ? '#15803D' : '#A16207',
                                  '& .MuiChip-label': { px: 1 },
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {/* Mirror the lab-side "Enter Results" call to
                                  action — same teal→green gradient button so
                                  the radiology and laboratory dashboards feel
                                  like one product. */}
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleEditScan(request)}
                                sx={{
                                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  px: 2.5,
                                  boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #0e8679 0%, #2ed968 100%)',
                                    boxShadow: '0 6px 16px rgba(17, 153, 142, 0.4)'
                                  }
                                }}
                              >
                                Enter Results
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={todayRequestsList.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                />
              </Box>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ScanHome;
